import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@artosera/ui';
import { usePeut } from '../../../../auth/usePeut.js';
import { estElevationAnnulee, useElevation } from '../../../../components/useElevation.js';
import {
  basculerOuverture,
  cocherControleRecette,
  obtenirRecetteOuverture,
  poserDerogationRecette,
} from '../../../../lib/api.js';
import { cleOuverture, messageErreurOuverture } from '../../../../lib/ouvertures.js';
import { cleRecette } from '../../../../lib/recette.js';
import type { ControleRecette, DossierOuverture, RecetteOuverture } from '../../../../types.js';
import pageStyles from '../../../../styles/page.module.css';
import styles from './Recette.module.css';
import { BasculeBloc } from './BasculeBloc.js';
import { EtatLots } from './EtatLots.js';
import { ListeControle } from './ListeControle.js';
import { TableauControle } from './TableauControle.js';
import { ValidationGalerie } from './ValidationGalerie.js';

/** Les états où la liste de contrôle se coche (l'API refuse les autres). */
const ETATS_CONTROLES: readonly string[] = ['reprise', 'recette', 'bascule_planifiee'];

/**
 * L'onglet « Recette et bascule » de la fiche dossier (ARTO-134, spec § 7.1,
 * 8.11, 8.12). Le tableau de contrôle d'abord, puis l'état des lots, la liste
 * de contrôle, la validation de la galerie et la bascule. Chaque écriture de
 * la recette fait avancer la révision du dossier : il est relu à leur issue,
 * sinon la prochaine transition échouerait sur une révision périmée.
 */
export function RecetteBascule({ dossier }: { dossier: DossierOuverture }): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const { toast } = useToast();
  const elevation = useElevation();
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const cle = 'ouvertures.fiche.recette';

  const requete = useQuery({
    queryKey: cleRecette(dossier.id),
    queryFn: () => obtenirRecetteOuverture(dossier.id),
    enabled: dossier.galleryId !== null,
    // Une bascule qui tourne se relit, pour voir ses étapes se cocher.
    refetchInterval: (courante) => (courante.state.data?.bascule.enCours ? 3000 : false),
  });

  if (dossier.galleryId === null) {
    return <p className={pageStyles.videEtat}>{t(`${cle}.attente`)}</p>;
  }
  if (requete.isLoading) return <p className={pageStyles.videEtat}>{t(`${cle}.chargement`)}</p>;
  if (requete.isError || !requete.data) {
    return (
      <p className={styles.erreur} role="alert">
        {requete.error ? messageErreurOuverture(t, requete.error) : t(`${cle}.erreurChargement`)}
      </p>
    );
  }

  const recette = requete.data;
  const peutCocher = peut('console:manage-onboarding') && ETATS_CONTROLES.includes(recette.etat);
  const peutDirection = peut('console:manage-mandate');

  /** Une écriture : l'annulation du second facteur se tait, toute autre erreur s'affiche. */
  const ecrire = async <T,>(
    geste: () => Promise<T>,
    suivi: (resultat: T) => void,
  ): Promise<boolean> => {
    setErreur(null);
    setEnCours(true);
    try {
      suivi(await geste());
      return true;
    } catch (echec) {
      if (!estElevationAnnulee(echec)) setErreur(messageErreurOuverture(t, echec));
      return false;
    } finally {
      setEnCours(false);
      // La révision du dossier et la recette ont pu bouger, y compris sur un échec.
      void client.invalidateQueries({ queryKey: cleOuverture(dossier.id) });
      void client.invalidateQueries({ queryKey: ['console', 'ouvertures'] });
    }
  };
  const garderRecette = (suivante: RecetteOuverture): void => {
    client.setQueryData(cleRecette(dossier.id), suivante);
  };
  const annoncer = (titre: string): void =>
    toast({ title: titre, closeLabel: t('saisies.suppression.fermer') });

  const changerControle = (controle: ControleRecette, coche: boolean, note: string): void => {
    void ecrire(
      () =>
        cocherControleRecette(dossier.id, {
          controle,
          coche,
          ...(coche && note.trim() !== '' ? { note: note.trim() } : {}),
        }),
      (suivante) => {
        garderRecette(suivante);
        annoncer(t(`${cle}.controles.${coche ? 'cochee' : 'decochee'}`));
      },
    );
  };

  const deroger = (motif: string): Promise<boolean> =>
    ecrire(
      () => elevation.executer(() => poserDerogationRecette(dossier.id, motif)),
      (suivante) => {
        garderRecette(suivante);
        annoncer(t(`${cle}.derogation.ok`));
      },
    );

  const basculer = (): Promise<boolean> =>
    ecrire(
      () => elevation.executer(() => basculerOuverture(dossier.id)),
      () => annoncer(t(`${cle}.bascule.ok`)),
    );

  return (
    <div className={styles.pile}>
      {/* Une bascule arrêtée se dit dans son propre bloc, avec l'étape : pas deux fois. */}
      {erreur && recette.bascule.erreur === null ? (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}
      {!peut('console:manage-onboarding') ? (
        <p className={styles.discret}>{t(`${cle}.lectureSeule`)}</p>
      ) : null}

      <TableauControle recette={recette} />
      <EtatLots lots={recette.lots} galleryId={dossier.galleryId} />
      <ListeControle
        controles={recette.controles}
        modifiable={peutCocher}
        enCours={enCours}
        onChanger={changerControle}
      />
      <ValidationGalerie
        recette={recette}
        peutDeroger={peutDirection}
        enCours={enCours}
        surDerogation={deroger}
        boiteMasquee={elevation.enElevation}
      />
      <BasculeBloc
        dossier={dossier}
        recette={recette}
        peutBasculer={peutDirection}
        enCours={enCours}
        surBascule={basculer}
        boiteMasquee={elevation.enElevation}
      />
      {elevation.dialogue}
    </div>
  );
}
